"use client";

import { useState, useEffect } from "react";
import { MaskEffect } from "./MaskEffect";

// Einfache Props für Varianten
interface VariantProps {
  className?: string;
}

// Hook um Theme-Änderungen zu beobachten
const useTheme = () => {
  const [isDark, setIsDark] = useState(() => {
    if (typeof document === "undefined") return true;
    return document.documentElement.classList.contains("dark");
  });

  useEffect(() => {
    const updateTheme = () => {
      setIsDark(document.documentElement.classList.contains("dark"));
    };

    // Initialen Wert nach Mount setzen
    updateTheme();

    // Auf Änderungen hören
    const observer = new MutationObserver(updateTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });

    return () => observer.disconnect();
  }, []);

  return isDark;
};

// ========================================
// STARTSEITE / HOMEPAGE - BITCOIN
// ========================================
export function MaskEffectHomepage({ className }: VariantProps) {
  return (
    <MaskEffect
      normalText={["Bitcoin", "Das Echo", "der Freiheit"]}
      shadowText={["Fiat", "Die Illusion", "der Freiheit"]}
      greyIndices={[]}
      bitcoinIndices={[0]} // Bitcoin in Bitcoin-Orange
      normalGradient="#0d0d0d"
      shadowGradient="#F7931A" // Bitcoin Orange
      className={className}
      // Auf der Landingpage übernimmt der globale LandingMaskOverlay den Reveal-Effekt.
      // Hier nur der Vordergrund-Text mit Pressed-In/Flicker, ohne eigene Cursor-Mask.
      flatMode={true}
    />
  );
}

// ========================================
// IMPRESSUM / LEGAL
// ========================================
export function MaskEffectLegal({ className }: VariantProps) {
  const isDark = useTheme();

  return (
    <MaskEffect
      normalText={["Impressum", "&", "Datenschutz"]}
      shadowText={["Made", "in", "Germany"]}
      greyIndices={[]}
      bitcoinIndices={[]}
      normalGradient={isDark ? "#030712" : "#f3f4f6"}
      shadowGradient="#F7931A" // Bitcoin Orange
      textColor={isDark ? "#b7ab98" : "#374151"} // Beige (dark) / Gray-700 (light)
      className={className}
    />
  );
}

// ========================================
// AGB
// ========================================
export function MaskEffectTerms({ className }: VariantProps) {
  const isDark = useTheme();

  return (
    <MaskEffect
      normalText={["AGB"]}
      shadowText={["don't trust", "verify"]}
      greyIndices={[]}
      bitcoinIndices={[]}
      normalGradient={isDark ? "#030712" : "#f3f4f6"}
      shadowGradient="#F7931A" // Bitcoin Orange
      textColor={isDark ? "#b7ab98" : "#374151"}
      className={className}
    />
  );
}

// ========================================
// PRODUKTPALETTE
// ========================================
export function MaskEffectProducts({ className }: VariantProps) {
  return (
    <MaskEffect
      normalText={["Unsere", "Trading", "Produkte"]}
      shadowText={["Premium", "Solutions", "Pro"]}
      greyIndices={[]}
      normalGradient="linear-gradient(to bottom right, #f0fdf4, #f0fdfa, #eff6ff)"
      shadowGradient="#F7931A" // Bitcoin Orange
      className={className}
    />
  );
}

// ========================================
// PREISE / PRICING
// ========================================
export function MaskEffectPricing({ className }: VariantProps) {
  return (
    <MaskEffect
      normalText={["Preise", "&", "Pakete"]}
      shadowText={["Plans", "&", "Pricing"]}
      greyIndices={[1]}
      normalGradient="linear-gradient(to bottom right, #ecfdf5, #f0fdf4, #f0fdfa)"
      shadowGradient="linear-gradient(to right, #10b981, #22c55e, #14b8a6)"
      className={className}
    />
  );
}

// ========================================
// KONTAKT / CONTACT
// ========================================
export function MaskEffectContact({ className }: VariantProps) {
  return (
    <MaskEffect
      normalText={["Kontakt", "&", "Support"]}
      shadowText={["Contact", "Us", "Now"]}
      greyIndices={[1]}
      normalGradient="linear-gradient(to bottom right, #fff7ed, #fef2f2, #fdf2f8)"
      shadowGradient="linear-gradient(to right, #f97316, #ef4444, #ec4899)"
      className={className}
    />
  );
}

// ========================================
// ÜBER UNS / ABOUT
// ========================================
export function MaskEffectAbout({ className }: VariantProps) {
  return (
    <MaskEffect
      normalText={["Über", "Uns"]}
      shadowText={["About", "Team"]}
      greyIndices={[]}
      normalGradient="linear-gradient(to bottom right, #fffbeb, #fefce8, #fff7ed)"
      shadowGradient="linear-gradient(to right, #f59e0b, #eab308, #f97316)"
      className={className}
    />
  );
}

// ========================================
// ACADEMY / BILDUNG
// ========================================
export function MaskEffectAcademy({ className }: VariantProps) {
  return (
    <MaskEffect
      normalText={["Trading", "Academy", "Kurse"]}
      shadowText={["Learn", "Grow", "Succeed"]}
      greyIndices={[]}
      normalGradient="linear-gradient(to bottom right, #faf5ff, #fdf2f8, #fff1f2)"
      shadowGradient="linear-gradient(to right, #a855f7, #ec4899, #f43f5e)"
      className={className}
    />
  );
}

// ========================================
// BLOG / NEWS
// ========================================
export function MaskEffectBlog({ className }: VariantProps) {
  return (
    <MaskEffect
      normalText={["Markt", "News", "& Insights"]}
      shadowText={["Latest", "Analysis", "Updates"]}
      greyIndices={[2]}
      normalGradient="linear-gradient(to bottom right, #ecfeff, #f0f9ff, #eff6ff)"
      shadowGradient="linear-gradient(to right, #06b6d4, #0ea5e9, #3b82f6)"
      className={className}
    />
  );
}

// ========================================
// CUSTOM VARIANTE
// ========================================
interface CustomMaskEffectProps {
  normalText: string[];
  shadowText: string[];
  greyIndices?: number[];
  theme?: "blue" | "green" | "purple" | "orange" | "gray" | "red";
  className?: string;
}

export function MaskEffectCustom({
  normalText,
  shadowText,
  greyIndices = [],
  theme = "blue",
  className,
}: CustomMaskEffectProps) {
  const themes = {
    blue: {
      normal: "linear-gradient(to bottom right, #eff6ff, #eef2ff)",
      shadow: "linear-gradient(to right, #2563eb, #4f46e5)",
    },
    green: {
      normal: "linear-gradient(to bottom right, #f0fdf4, #f0fdfa)",
      shadow: "linear-gradient(to right, #22c55e, #14b8a6)",
    },
    purple: {
      normal: "linear-gradient(to bottom right, #faf5ff, #fdf2f8)",
      shadow: "linear-gradient(to right, #a855f7, #ec4899)",
    },
    orange: {
      normal: "linear-gradient(to bottom right, #fff7ed, #fef2f2)",
      shadow: "linear-gradient(to right, #f97316, #ef4444)",
    },
    gray: {
      normal: "linear-gradient(to bottom right, #f9fafb, #f3f4f6)",
      shadow: "linear-gradient(to right, #4b5563, #1f2937)",
    },
    red: {
      normal: "linear-gradient(to bottom right, #fef2f2, #fdf2f8)",
      shadow: "linear-gradient(to right, #ef4444, #ec4899)",
    },
  };

  return (
    <MaskEffect
      normalText={normalText}
      shadowText={shadowText}
      greyIndices={greyIndices}
      normalGradient={themes[theme].normal}
      shadowGradient={themes[theme].shadow}
      className={className}
    />
  );
}
